import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext'; // Votre hook existant
import orderService, { Order } from '../services/orderService';
import { Bell, ShoppingBag, Clock, CheckCircle, X } from 'lucide-react';

interface OrderNotificationsProps {
  className?: string;
}

const OrderNotifications: React.FC<OrderNotificationsProps> = ({ className = '' }) => {
  const { user } = useAuth(); // Votre hook existant
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [lastChecked, setLastChecked] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id && user.is_farmer) {
      loadRecentOrders();
      // Vérifier les nouvelles commandes toutes les 30 secondes
      const interval = setInterval(loadRecentOrders, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const loadRecentOrders = async () => {
    try {
      const orders = await orderService.getFarmerRecentOrders(user!.id, 10);
      setRecentOrders(orders);
      
      // Calculer le nombre de nouvelles commandes
      const lastCheckedTime = lastChecked ? new Date(lastChecked) : new Date(Date.now() - 24 * 60 * 60 * 1000);
      const newOrders = orders.filter(order => new Date(order.created_at) > lastCheckedTime);
      setNewOrdersCount(newOrders.length);
    } catch (error) {
      console.error('Error loading recent orders:', error);
    }
  };

  const markAsChecked = () => {
    setLastChecked(new Date().toISOString());
    setNewOrdersCount(0);
    localStorage.setItem('lastOrderCheck', new Date().toISOString());
  };

  // Charger la dernière vérification au démarrage
  useEffect(() => {
    const stored = localStorage.getItem('lastOrderCheck');
    if (stored) {
      setLastChecked(stored);
    }
  }, []);

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (!showNotifications) {
      markAsChecked();
    }
  };

  const getOrderStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'confirmed':
      case 'delivered':
        return <CheckCircle className="h-4 w-4 text-olive" />;
      default:
        return <ShoppingBag className="h-4 w-4 text-terracotta" />;
    }
  };

  if (!user?.is_farmer) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      {/* Notification Bell */}
      <button
        onClick={toggleNotifications}
        className="relative p-2 text-slate hover:text-terracotta hover:bg-gray-100 rounded-full transition-colors"
      >
        <Bell className="h-6 w-6" />
        {newOrdersCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-terracotta text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {newOrdersCount > 9 ? '9+' : newOrdersCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate">Commandes récentes</h3>
              <button
                onClick={() => setShowNotifications(false)}
                className="text-gray-400 hover:text-slate"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {recentOrders.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <ShoppingBag className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p>Aucune commande récente</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recentOrders.map((order) => (
                  <div key={order.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        {getOrderStatusIcon(order.status)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate">
                            Commande #{order.id}
                          </p>
                          <p className="text-sm text-gray-600">
                            Client #{order.user_id}
                          </p>
                          <p className="text-xs text-gray-500">
                            {orderService.formatDate(order.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate">
                          {orderService.formatPrice(order.total_price)}
                        </p>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${orderService.getStatusColor(order.status)}`}>
                          {orderService.getStatusLabel(order.status)}
                        </span>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="mt-2 flex items-center space-x-2">
                      <button
                        onClick={() => window.location.href = '/farmer/orders'}
                        className="text-xs text-olive hover:text-terracotta"
                      >
                        Voir détails
                      </button>
                      {order.status === 'pending' && (
                        <button
                          onClick={async () => {
                            try {
                              await orderService.updateOrderStatus(order.id, user.id, 'confirmed');
                              loadRecentOrders(); // Recharger les commandes
                            } catch (error) {
                              console.error('Error confirming order:', error);
                            }
                          }}
                          className="text-xs text-olive hover:text-terracotta"
                        >
                          Confirmer
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {recentOrders.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => window.location.href = '/farmer/orders'}
                className="w-full text-center text-sm text-olive hover:text-terracotta font-medium"
              >
                Voir toutes les commandes
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderNotifications;